import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import KPI from "Samarth Module/components/KPI/kpi";
import DispatchTable from "./DispatchTable.js";
import MinMaxDispatches from "./MinMaxDispatches.js";
import BGPrioritization from "./BGPrioritization.js";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput.js";
import Threedots from './../../../../assets/images/PhosphorLine.svg';
import Filters from "./../../../../assets/images/filters.svg";
import Refresh from './../../../../assets/images/ArrowCounterClockwise.svg';
import styles from './dispatchPriority.module.css';
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu.js";
import DateRangePickerFilter from "Samarth Module/components/DateRangePicker/DateRangePickerFilter.js";
import { fetchDispatchFilters, fetchDispatchOverviewTable, fetchDispatchPriorityKPI, fetchDispatchPriorityMinMaxGraph, fetchGoodnessGraph, handleOverviewDispatchExport, fetchDCwisePrioritizationSuggested } from "Samarth Module/api/Qcommapi.js";
import KPIImg from './../../../../assets/images/Phosphor.svg';
import { CircularProgress } from "@mui/material";
import MoreFilters from "Samarth Module/components/drawers/moreFilters.js";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns.js";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar.js";

const DispatchPriority = ({ sideTab, handleFilterSelection }) => {
    const { accounts } = useMsal();
    const activeAccount = accounts[0];
    const [filters, setFilters] = useState({
        dispatchPriorityFiltersData: [],
        defaultColsOverviewTable: [],
    });
    const [dateRange, setDateRange] = useState(null);
    const [dispatchPriorityData, setDispatchPriorityData] = useState({
        minMaxData: [],
        kpiData: [],
        overviewTableData: []
    });
    const [loading, setLoading] = useState({
        initialLoading: true,
        kpiLoading: false,
        minMaxLoading: false,
        goodnessGraphLoading: false,
        overviewTableLoading: false,
    });
    const [isDrawerOpen, setIsDrawerOpen] = useState({
        filterDrawer: false,
        customiseDrawer: false,
    });
    const [sortingData, setSortingData] = useState({
        sortCol: null,
        sortDirection: null,
    });
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: "",
        type: "error",
    });
    const clearFiltersRef = useRef(false);

    const fetchInitialData = async (dispatchPriorityFilters = []) => {
        const shouldClearFilters = clearFiltersRef.current;
        clearFiltersRef.current = false;
        
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: dispatchPriorityFilters.reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "Dispatch Priority",
                clearFilters: shouldClearFilters,
            };

            const dispatchPriorityFiltersData = await fetchDispatchFilters(payload, accounts[0].idToken);

            setFilters(prev => ({
                ...prev,
                dispatchPriorityFiltersData: dispatchPriorityFiltersData["filters"] || [],
                defaultColsOverviewTable: dispatchPriorityFiltersData["defaultColumns"] || [],
            }));
            fetchData(dispatchPriorityFiltersData["filters"]);
        } catch (error) {
            console.error("Error fetching initial filter data:", error);
        } finally {
            setLoading(prev => ({ ...prev, "initialLoading": false }));
        }
    };

    // Fetch all required data (except filters)
    const fetchData = async (dispatchPriorityFilters = []) => {
        setLoading(prev => ({
            ...prev,
            kpiLoading: true,
            minMaxLoading: true,
            goodnessGraphLoading: true,
            overviewTableLoading: true,
        }));
        try {
            const payload = {
                email: activeAccount.username,
                uom: sideTab || 'Tonnes',
                filters: dispatchPriorityFilters.reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                type: "Dispatch Priority",
            };

            // Execute API calls in parallel (excluding fetchDispatchSuggestedOrderFilters)
            const [kpiData, minMaxGraph, goodnessGraphData, OverviewTableData, dcwisePrioritizationData] = await Promise.all([
                fetchDispatchPriorityKPI(payload, accounts[0].idToken),
                fetchDispatchPriorityMinMaxGraph(payload, accounts[0].idToken),
                fetchGoodnessGraph(payload, accounts[0].idToken),
                fetchDispatchOverviewTable({ ...payload, page: 1, pageSize: dispatchPriorityData?.["paginationData"]?.["rowsPerPage"] || 15 }, accounts[0].idToken),
                fetchDCwisePrioritizationSuggested(payload, accounts[0].idToken),
            ]);
            setDispatchPriorityData({
                kpiData: kpiData.map(kpi => ({
                    ...kpi,
                    img: KPIImg
                })),
                minMaxData: minMaxGraph["data"].map(item => ({ ...item, isExpanded: false })) || [],
                goodNessGraph: goodnessGraphData || [],
                overviewTableData: OverviewTableData["data"] || [],
                paginationData: OverviewTableData["pagination"] || null,
                dcwisePrioritizationData: dcwisePrioritizationData || [],
            });
        } catch (error) {
            setDispatchPriorityData({
                kpiData: [],
                minMaxData: [],
                goodNessGraph: [],
                overviewTableData: [],
                dcwisePrioritizationData: [],
            });
            console.error("Error fetching data:", error);
        } finally {
            setLoading(prev => ({
                ...prev,
                initialLoading: false,
                kpiLoading: false,
                minMaxLoading: false,
                goodnessGraphLoading: false,
                overviewTableLoading: false,
            }));
        }
    };

    useEffect(() => {
        if (window.location.pathname === '/samarth/dispatch_priority') {
            fetchInitialData();
        }
    }, []);

    // Fetch data when filters or sideTab changes
    useEffect(() => {
        if (!loading.initialLoading && filters.dispatchPriorityFiltersData.length > 0) {
            fetchData();
        }
    }, [sideTab]);

    // Function to get all data keys from filterOptions
    const getAllFilterData = () => {
        const allData = [];
        filters.forEach(filter => {
            if (filter.data && Array.isArray(filter.data)) {
                allData.push(...filter.data);
            }
        });
        return allData;
    };

    // Function to get unique data keys from filterOptions
    const getAllUniqueFilterData = () => {
        const allData = [];
        filters.forEach(filter => {
            if (filter.data && Array.isArray(filter.data)) {
                allData.push(...filter.data);
            }
        });
        return [...new Set(allData)]; // Remove duplicates
    };

    // Function to get count of all data items
    const getTotalFilterDataCount = () => {
        let count = 0;
        filters.forEach(filter => {
            if (filter.data && Array.isArray(filter.data)) {
                count += filter.data.length;
            }
        });
        return count;
    };

    // Function to get data grouped by filter label
    const getFilterDataGrouped = () => {
        const grouped = {};
        filters.forEach(filter => {
            if (filter.data && Array.isArray(filter.data) && filter.data.length > 0) {
                grouped[filter.label] = filter.data;
            }
        });
        return grouped;
    };

    // Handle filter selection changes
    const handleFilterChange = (label, selectedOptions) => {
        let newFilter;
        setFilters(prevFilters => {
            newFilter = {
                ...prevFilters,
                dispatchPriorityFiltersData: prevFilters["dispatchPriorityFiltersData"].map(filter =>
                    filter.label === label
                        ? { ...filter, selectedData: selectedOptions }
                        : filter
                )
            }
            return newFilter;
        });
        handleFilterSelection("Dispatch Priority", label, selectedOptions);

        // Check if there are any selected filters
        const hasSelection = newFilter["dispatchPriorityFiltersData"].some((filter) => {
            const selected = filter?.selectedData;
            if (Array.isArray(selected)) return selected.length > 0;
            if (typeof selected === 'string') return selected.trim().length > 0;
            return selected !== undefined && selected !== null;
        });
        
        clearFiltersRef.current = !hasSelection;
        setSortingData({ sortCol: null, sortDirection: null });
        fetchInitialData(newFilter["dispatchPriorityFiltersData"]);
    };

    // Handle date range change
    const handleDateRangeChange = (dateRangeData) => {
        setDateRange(dateRangeData);
    };

    // Reset all filters - clear selectedData
    const resetFilter = () => {
        clearFiltersRef.current = true;

        setFilters(prevFilters => ({
            ...prevFilters,
            dispatchPriorityFiltersData: prevFilters["dispatchPriorityFiltersData"].map(filter => ({
                ...filter,
                selectedData: []
            }))
        }));
        setDateRange(null);
        // Reset pagination and sorting when filters reset
        setSortingData({ sortCol: null, sortDirection: null });
        fetchInitialData([]);
    };

    const toggleFilterDrawer = useCallback(() => {
        setIsDrawerOpen(prev => ({ ...prev, filterDrawer: !prev.filterDrawer }));
    }, []);

    const toggleCustomizeDrawer = useCallback(() => {
        setIsDrawerOpen(prev => ({ ...prev, customiseDrawer: !prev.customiseDrawer }));
    }, []);

    const overTableData = async (rowsPerPage, newPage, sortCol, sortDirection) => {
        // setLoading(prev => ({
        //     ...prev,
        //     "overviewTableLoading": true,
        // }));
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: filters["dispatchPriorityFiltersData"].reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "Dispatch Priority",
                "page": newPage,
                "pageSize": rowsPerPage,
            };
            if (sortCol && sortDirection) {
                payload.sortBy = sortCol;
                payload.sortOrder = sortDirection;
            }
            const overviewTableData = await fetchDispatchOverviewTable(payload, accounts[0].idToken);
            setDispatchPriorityData(prev => ({
                ...prev,
                overviewTableData: overviewTableData["data"] || [],
                paginationData: overviewTableData["pagination"] || null
            }));
        } catch (error) {
            setDispatchPriorityData(prev => ({
                ...prev,
                overviewTableData: [],
                paginationData: null
            }));
            console.error("Error fetching overview table data:", error);
        } finally {
            // setLoading(prev => ({
            //     ...prev,
            //     "overviewTableLoading": false,
            // }));
        }
    };

    const handleOverviewExport = async () => {
        setLoading(prev => ({
            ...prev,
            "exportLoading": true,
        }));
        // Implement export functionality here, using current filters and sorting data
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: filters["dispatchPriorityFiltersData"].reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "Dispatch Priority",
            };
            await handleOverviewDispatchExport(payload, accounts[0].idToken);
            setSnackBarObject({ snackbarOpen: true, message: "File exported successfully", type: 'success' });
        } catch (error) {
            console.error("Error exporting data:", error);
            setSnackBarObject({ snackbarOpen: true, message: "Error occured while exporting file. Please try again later.", type: 'error' });
        } finally {
            setLoading(prev => ({
                ...prev,
                "exportLoading": false,
            }));
        }
    };

    const closeSnackbar = useCallback(() => {
        setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
    }, []);

    return (
        <>
            {loading.initialLoading ?
                <div className={styles.loaderContainer}><CircularProgress /></div> :
                <div className={`${styles.container} ${loading.exportLoading ? styles.blurBackground : ''}`}>
                    <div className={styles.section}>
                        <KPI kpiCardData={dispatchPriorityData['kpiData']} sideTab={sideTab} />
                    </div>
                    <div className={styles.section}>
                        {filters.dispatchPriorityFiltersData.filter((filter) => filter.isFavourite).map((filter, index) => (
                            <div key={`${filter.label}-${index}`} className={styles.filterWrapper}>
                                <MultiSelectWithSearchInput
                                    label={filter.label}
                                    options={filter.data}
                                    filterValues={filter.selectedData}
                                    isMultiSelect={filter.isMultiSelect}
                                    handleFilter={handleFilterChange}
                                    type={"Dispatch Priority"}
                                />
                            </div>
                        ))}
                        {/* <DateRangePickerFilter
                            label={"PO Validity"}
                            value={dateRange}
                            onChange={handleDateRangeChange}
                        /> */}
                        <div className={styles.iconContainer}>
                            <img src={Refresh} className={styles.iconStyling} alt="refresh filter" onClick={resetFilter} />
                            <OverflowMenu
                                menuItems={[
                                    {
                                        label: 'More Filters',
                                        icon: Filters,
                                        onClick: toggleFilterDrawer,
                                    }
                                ]}
                                Icon={Threedots}
                            />
                        </div>
                    </div>
                    <div className={styles.middlePageContainer}>
                        <div className={styles.halfSection}>
                            {loading["minMaxLoading"] ?
                                <div className={styles.loaderContainer}><CircularProgress /></div> :
                                <MinMaxDispatches label={"Min Max Dispatches for DC"} data={dispatchPriorityData["minMaxData"]} sideTabVal={sideTab} />
                            }
                        </div>
                        <div className={styles.halfSection}>
                            {loading["goodnessGraphLoading"] ?
                                <div className={styles.loaderContainer}><CircularProgress /></div> :
                                <BGPrioritization label={"Goodness Improvement"} goodnessData={dispatchPriorityData['goodNessGraph']["bg_wise_data"]} dcWiseData={dispatchPriorityData?.['dcwisePrioritizationData']?.['dc_wise_data']} kpiSummaryData={dispatchPriorityData['goodNessGraph']["kpi_summary"]} dcWiseKpiSummaryData={dispatchPriorityData?.['dcwisePrioritizationData']?.["kpi_summary"]} />
                            }
                        </div>
                    </div>
                    <div className={styles.overViewTableContainer}>
                        {loading["overviewTableLoading"] ?
                            <div className={styles.loaderContainer}><CircularProgress /></div> :
                            <DispatchTable
                                tableData={dispatchPriorityData["overviewTableData"]}
                                paginationData={dispatchPriorityData["paginationData"]}
                                label={"Overview"}
                                columns={filters["defaultColsOverviewTable"]}
                                handleOpenCustomization={toggleCustomizeDrawer}
                                sortingData={sortingData}
                                handlePageChange={(rowsPerPage,newPage, sortCol, sortDirection) => {
                                    if (sortCol && sortDirection) {
                                        setSortingData({ sortCol, sortDirection });
                                    }
                                    overTableData(rowsPerPage, newPage, sortCol, sortDirection);
                                }}
                                handleExport={handleOverviewExport}
                            />
                        }
                    </div>
                    {isDrawerOpen.filterDrawer &&
                        <MoreFilters
                            anchor={'right'}
                            filterDrawer={isDrawerOpen.filterDrawer}
                            handleFilterDrawer={toggleFilterDrawer}
                            rmpmFilters={filters["dispatchPriorityFiltersData"]}
                            setRmpmFilters={(filters) => {
                                setFilters(prevFilters => ({
                                    ...prevFilters,
                                    dispatchPriorityFiltersData: filters
                                }));
                                fetchInitialData(filters);
                            }}
                            username={activeAccount.username}
                            type={"Dispatch Priority"}
                            limit={9}
                        />
                    }
                    {isDrawerOpen["customiseDrawer"] &&
                        <CustomiseColumns
                            anchor={'right'}
                            columns={[...filters["defaultColsOverviewTable"]]}
                            isOpen={isDrawerOpen["customiseDrawer"]}
                            handleCustomizeDrawer={toggleCustomizeDrawer}
                            columnCustomization={(customizedColumns) => {
                                setFilters(prevFilters => ({
                                    ...prevFilters,
                                    defaultColsOverviewTable: customizedColumns
                                }));
                                toggleCustomizeDrawer();
                            }}
                            username={activeAccount.username}
                            type={"Dispatch Priority"}
                        />
                    }
                </div>
            }
            {loading["exportLoading"] && <div className={styles.loaderExcelContainer}><CircularProgress /></div>}
            {snackBarObject.snackbarOpen && (
                <SnackBarMessage
                    type={snackBarObject.type}
                    snackbarMessage={snackBarObject.message}
                    snackbarOpen={snackBarObject.snackbarOpen}
                    setSnackbarOpen={closeSnackbar}
                />
            )}
        </>
    );
};
export default DispatchPriority;
